import config from '../config';
import { authFetch as fetch } from '../common/authentication';
import { isNullOrEmpty } from '../common/utils';
import {
  GetUploadDetailsResponse,
  GetUploadUrlResponse,
  GetBQTriggerResponse,
  GetLogUrlResponse,
} from '../common/domain/responses';
import {
  SubmitMappingRequest,
  SubmitVerificationApproval,
} from '../common/domain/requests';

export const getUploadUrl = (request: {
  fileName: string;
  groupId: string;
  uploadType: {
    id: string;
    internalType: string;
    name: string;
    internalId: string;
  };
}) => {
  return new Promise<GetUploadUrlResponse>((resolve, reject) => {
    fetch(config.endpoints.getUploadUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    })
      .then((response) => response.json())
      .then(resolve)
      .catch(reject);
  });
};

export const cancelIngest = (ingestId?: string, groupId?: string) => {
  return new Promise((resolve, reject) => {
    if (isNullOrEmpty(ingestId)) {
      reject(new Error(''));
    }
    fetch(config.endpoints.cancelIngest, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ingestId: ingestId,
        groupId: groupId,
      }),
    })
      .then(resolve)
      .catch(reject);
  });
};

export const uploadFile = (
  url: string,
  file: File,
  onprogress: (loaded: number, total: number) => void,
) => {
  return new Promise<void>((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.upload.onprogress = (e) => {
      onprogress(e.loaded, e.total);
    };
    request.upload.onload = () => {
      resolve();
    };
    request.open('PUT', url);
    request.setRequestHeader('Content-Type', 'text/csv');
    request.send(file);
  });
};

export const pollUploadValidation = (
  ingestId: string,
  groupId: string,
  checkSuccess: (result: GetUploadDetailsResponse) => boolean,
  checkError: (result: GetUploadDetailsResponse) => boolean,
) => {
  return new Promise<GetUploadDetailsResponse>((resolve, reject) => {
    const poll = () => {
      fetch(
        `${config.endpoints.uploadDetails}?ingestId=${encodeURI(
          ingestId,
        )}&groupId=${encodeURI(groupId)}`,
      )
        .then((res) => res.json())
        .then((result) => {
          if (checkSuccess(result)) {
            resolve(result);
            return;
          }
          if (checkError(result)) {
            reject(result);
            return;
          }
          setTimeout(poll, config.uploadValidationPollInterval);
        });
    };
    poll();
  });
};

export const pollBQIngest = (
  ingestId: string,
  groupId: string,
  checkSuccess: (result: GetBQTriggerResponse) => boolean,
  checkError: (result: GetBQTriggerResponse) => boolean,
) => {
  return new Promise<GetBQTriggerResponse>((resolve, reject) => {
    const poll = () => {
      fetch(
        `${config.endpoints.uploadDetails}?ingestId=${encodeURI(
          ingestId,
        )}&groupId=${encodeURI(groupId)}`,
      )
        .then((res) => res.json())
        .then((result) => {
          if (checkSuccess(result)) {
            resolve(result);
            return;
          }
          if (checkError(result)) {
            reject(result);
            return;
          }
          setTimeout(poll, config.uploadValidationPollInterval);
        });
    };
    poll();
  });
};

export const submitMapping = (mapping: SubmitMappingRequest) => {
  return new Promise((resolve, reject) => {
    fetch(config.endpoints.mapUpload, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(mapping),
    })
      .then((res) => res.json())
      .then((json) => resolve(json))
      .catch((res) => res.json())
      .then((json) => reject(json));
  });
};

export const approveVerificationReport = (
  approval: SubmitVerificationApproval,
) => {
  return new Promise((resolve, reject) => {
    fetch(config.endpoints.approveVerificationReport, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(approval),
    })
      .then((response) => response.json())
      .then((json) => resolve(json))
      .catch((response) => response.json())
      .then((json) => reject(json));
  });
};

export const pollMappingValidation = (
  ingestId: string,
  groupId: string,
  checkSuccess: (result: GetUploadDetailsResponse) => boolean,
  checkError: (result: GetUploadDetailsResponse) => boolean,
): Promise<GetUploadDetailsResponse> => {
  return pollUploadValidation(ingestId, groupId, checkSuccess, checkError);
};

export const pollIngestState = (
  ingestId: string,
  groupId: string,
  checkSuccess: (result: GetUploadDetailsResponse) => boolean,
  checkError: (result: GetUploadDetailsResponse) => boolean,
) => {
  return new Promise<GetUploadDetailsResponse>((resolve, reject) => {
    const poll = () => {
      fetch(
        `${config.endpoints.uploadDetails}?ingestId=${encodeURI(
          ingestId,
        )}&groupId=${encodeURI(groupId)}`,
      )
        .then((res) => res.json())
        .then((result) => {
          if (checkSuccess(result)) {
            resolve(result);
            return;
          }
          if (checkError(result)) {
            reject(result);
            return;
          }
          setTimeout(poll, config.uploadValidationPollInterval);
        });
    };
    poll();
  });
};

export const getLogUrl = (request: { groupId: string; ingestId: string }) => {
  return new Promise<GetLogUrlResponse>((resolve, reject) => {
    fetch(config.endpoints.getLogUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    })
      .then((response) => response.json())
      .then(resolve)
      .catch(reject);
  });
};

export const downloadBucketFile = (url, fileName) => {
  return fetch(url)
    .then((res) => res.blob())
    .then(
      (blob) =>
        new Promise<void>((resolve, reject) => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.download = fileName;
          a.href = url;
          document.body.appendChild(a);
          a.click();
          setTimeout(() => {
            document.body.removeChild(a);
          }, 1000);
          resolve();
        }),
    );
};

export const getLogStatus = (ingestId: string, groupId: string) => {
  return new Promise<boolean>((resolve, reject) => {
    fetch(
      `${config.endpoints.uploadDetails}?ingestId=${encodeURI(
        ingestId,
      )}&groupId=${encodeURI(groupId)}`,
    )
      .then((res) => res.json())
      .then((result) => {
        resolve(result.logStatus);
      })
      .catch((err) => {
        console.log(err);
        reject(err);
      });
  });
};
